import _ from 'lodash';

import('@amzn/ihm-coral-config/coral');
import { ServiceClient } from '@services/coralservices/ServiceClient';
import IhmSpyglassService from 'IhmSpyglassService-coral-javascript-clients/jquery-client.js';
import { AppInfoLoader, AssociateAppEvent } from '@src/services';
import { CountMetric } from '@src/metrics';

const client = new ServiceClient('IhmSpyglassService', IhmSpyglassService);

const OPERATION_LIST_CALL_NAWS = ['GetLastReceiveTime', 'GetPromoAsins'];

export enum AggregationType {
    EXPIRATION_AWARE = 'EXPIRATION_AWARE',
    EXPIRATION_WITH_RETAIL_PRICE_AWARE = 'EXPIRATION_WITH_RETAIL_PRICE_AWARE',
}

/**
 * IhmSpyglassServiceClient
 * This module is responsible for calling IhmSpyglassService APIs.
 */
export class IhmSpyglassServiceClient {
    public static sendRequest(operation: string, data: { [key: string]: any }, storeId: string) {
        const dataToSend: { [key: string]: any } = _.cloneDeep(data);
        dataToSend.storeId = storeId; // Include the storeId in the input for store isolation routing
        return client.sendRequest(
            {
                operation,
                data: dataToSend,
            },
            storeId,
        );
    }

    /**
     *   Returns inventory for the input location-id
     */
    public static getInventoryAtLocation(storeId, locationId, aggregationType) {
        return IhmSpyglassServiceClient.sendRequest(
            'GetInventoryAtLocation',
            {
                storeId,
                entityId: locationId,
                aggregationType,
            },
            storeId,
        );
    }

    /**
     *   Returns Returns the last receive time of an asin in a store when given a time range.
     */
    public static getLastReceiveTimeStamp(storeId: string, fromTimeMs: number, toTimeMs: number, asin: string) {
        return IhmSpyglassServiceClient.sendRequest(
            'GetLastReceiveTime',
            {
                storeId,
                fromTimeMs,
                toTimeMs,
                asin,
            },
            storeId,
        );
    }

    /**
     *   Returns inventory with location for the input item-id.
     *   If aggregationType is not provided, Spyglass will use the DEFAULT aggregation.
     */
    public static getInventoryForItemId(storeId, itemIdWithType, aggregationType?) {
        return IhmSpyglassServiceClient.sendRequest(
            'GetInventoryForItemId',
            {
                storeId,
                itemIdWithType,
                ...(aggregationType && { aggregationType }),
            },
            storeId,
        );
    }

    /**
     *   Verifies and corrects the sellable inventory at the location and deletes
     *   damaged/expired from associates inventory.
     *   Used by Cycle count tool
     */
    public static ensureInventoryCountsAtLocation(storeId: string, locationId: string, verifiedCount: object, authId: string, eventId: string, reason: string) {
        return AssociateAppEvent.constructAppEvent().then(function (appEvent) {
            if (eventId) {
                appEvent.eventId = eventId;
            }
            return IhmSpyglassServiceClient.sendRequest(
                'EnsureInventoryCountsAtLocation',
                {
                    storeId,
                    locationId,
                    verifiedCount,
                    authId,
                    associateAppEvent: appEvent,
                    reason,
                },
                storeId,
            ).then((data) => {
                AppInfoLoader.getAppConfig().then((configData) => {
                    new CountMetric(storeId, 'ItemsCounted', 'IhmFlyingDutchman', configData.toolName) // @ts-ignore
                        .count(verifiedCount.sellable);
                });
                return data;
            });
        });
    }

    public static getAsinsForExternalItemId(storeId: string, externalId: string, externalIdTypes: any, disambiguateAsins?: boolean) {
        const data = {
            storeId,
            externalId,
            externalIdTypes,
            ...(disambiguateAsins && { disambiguateAsins }),
        };
        return IhmSpyglassServiceClient.sendRequest('GetAsinsForExternalItemId', data, storeId);
    }

    public static getSkusForItemId(storeId, id, includeOnlyActiveFnSkus, idTypes, requestedTypes) {
        const data = {
            storeId,
            id,
            includeOnlyActiveFnSkus,
            idTypes,
            requestedTypes,
        };
        return IhmSpyglassServiceClient.sendRequest('GetSkusForItemId', data, storeId);
    }

    public static getAsinForFcSku(storeId: string, fcSku: string) {
        const data = {
            storeId,
            fcSku,
        };
        return IhmSpyglassServiceClient.sendRequest('GetAsinForFcSku', data, storeId);
    }

    public static getProductInfo(
        storeId: string,
        itemIdWithType: { [key: string]: string },
        facetIds: string[],
        marketplaceId?: string,
        merchantId?: string,
        almStoreRegionId?: string,
    ) {
        const data = {
            storeId,
            itemIdWithType,
            facetIds,
            marketplaceId,
            merchantId,
            almStoreRegionId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetProductInfo', data, storeId);
    }

    public static getProductSpacesByItemId(storeId: string, itemIdWithType: { [key: string]: string }) {
        const data = {
            storeId,
            itemIdWithType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetProductSpacesByItemId', data, storeId);
    }

    public static getProductSpacesByItemIdBatch(storeId: string, itemIdsWithType: Array<{ [key: string]: string }>) {
        const data = {
            storeId,
            itemIdsWithType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetProductSpacesByItemIdBatch', data, storeId);
    }

    public static searchProductsByKeyword(
        storeId: string,
        searchKeywordType: string,
        searchKeyword: string,
        maxNumberOfResults: number,
        excludeProductsWithoutCategory?: string,
        searchIndex?: number,
        searchAlias?: string,
        marketplaceId?: string,
        merchantId?: string,
        asins?: string[],
    ) {
        return IhmSpyglassServiceClient.sendRequest(
            'SearchProductsByKeyword',
            {
                storeId,
                searchKeywordType,
                searchKeyword,
                maxNumberOfResults,
                excludeProductsWithoutCategory,
                searchIndex,
                searchAlias,
                marketplaceId,
                merchantId,
                asins,
            },
            storeId,
        );
    }

    /**
     * Returns a promise to a list of product spaces that contained in the
     * specified entityId as per the store plan
     * TODO: This needs to be removed from Spyglass
     */
    public static getProductSpacesInEntity(storeId: string, entityId: string) {
        const data = {
            storeId,
            entityId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetProductSpacesInEntity', data, storeId);
    }

    /**
     * Validates whether or not a group of products should be allowed to be
     * virtually moved to the given inventory location, based on a pre-configured
     * subset of business rules surrounding product and associate safety.
     *
     * @param itemsToMove - A list of items desired to be moved into the inventory
     * location, formatted like so:
     *      {
     *          itemIdWithType: com.amazon.ihm.id.coral#ItemIdWithType,
     *          quantity: com.amazon.ihm.units.coral#DecimalMeasure
     *      }
     *
     */
    public static validateMove(storeId, inventoryLocationId, itemsToMove) {
        const data = {
            storeId,
            inventoryLocationId,
            itemsToMove,
        };
        return IhmSpyglassServiceClient.sendRequest('ValidateMove', data, storeId);
    }

    /**
     * Returns a promise to a list of getProductInfoBatchResults.
     * The returned object is a list where each entry has an asin property
     * and a facet property which is a map of facetId to facet as defined in
     * com.amazon.ihmspyglassservice#FacetMap.
     */

    public static getProductInfoBatch(storeId, itemIdWithTypeList, facetIds, marketplaceId, merchantId, almStoreRegionId?) {
        const data = {
            storeId,
            itemIdWithTypeList,
            facetIds,
            marketplaceId,
            merchantId,
            almStoreRegionId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetProductInfoBatch', data, storeId);
    }

    /**
     * Receives the inventory into the given location.
     * The result should be an empty object unless an error occurs.
     * TODO: [IHMISIM-2956] Remove this function after all clients switch to receiveInventoryForItemIdWithType
     */
    public static receiveInventory(storeId, asin, quantity, locationId, expirationDate, eventId) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            if (eventId) {
                appEvent.eventId = eventId;
            }
            return IhmSpyglassServiceClient.sendRequest(
                'ReceiveInventory',
                {
                    storeId,
                    asin,
                    quantity,
                    locationId,
                    expirationDate,
                    associateAppEvent: appEvent,
                },
                storeId,
            ).then((data) => {
                AppInfoLoader.getAppConfig().then((configData) => {
                    new CountMetric(storeId, 'ItemsReceived', 'IhmFlyingDutchman', configData.toolName).count(quantity.value);
                });
                return data;
            });
        });
    }

    /**
     * Receives the inventory into the given location.
     * The result should be an empty object unless an error occurs.
     * Identical to ReceiveInventory except it
     * passes itemIdWithType instead of ASIN.
     */
    public static receiveInventoryForItemIdWithType(storeId, itemIdWithType, quantity, locationId, expirationDate, eventId) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            if (eventId) {
                appEvent.eventId = eventId;
            }
            return IhmSpyglassServiceClient.sendRequest(
                'ReceiveInventory',
                {
                    storeId,
                    itemIdWithType,
                    quantity,
                    locationId,
                    expirationDate,
                    associateAppEvent: appEvent,
                },
                storeId,
            ).then((data) => {
                AppInfoLoader.getAppConfig().then((configData) => {
                    new CountMetric(storeId, 'ItemsReceived', 'IhmFlyingDutchman', configData.toolName).count(quantity.value);
                });
                return data;
            });
        });
    }

    /**
     *  Get Transship Status.
     */
    public static getTransshipStatus(storeId, amazonShipmentRefId) {
        const data = {
            storeId,
            amazonShipmentRefId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetTransshipStatus', data, storeId);
    }

    /**
     *  Mark transferTote for reconciliation.
     */
    public static markTransferToteForReconciliation(storeId, toteId) {
        const data = {
            storeId,
            toteId,
        };
        return IhmSpyglassServiceClient.sendRequest('MarkTransferToteForReconciliation', data, storeId);
    }

    public static reopenTransferTote(storeId, toteId) {
        const data = {
            storeId,
            toteId,
        };
        return IhmSpyglassServiceClient.sendRequest('ReopenTransferTote', data, storeId);
    }

    public static createOutboundManifest(storeId, destinationWarehouseId) {
        const data = {
            storeId,
            destinationWarehouseId,
        };
        return IhmSpyglassServiceClient.sendRequest('CreateOutboundManifest', data, storeId);
    }

    public static editToteInOutboundManifest(storeId, amazonShipmentRefId, toteId, action) {
        const data = {
            storeId,
            amazonShipmentRefId,
            toteId,
            action,
        };
        return IhmSpyglassServiceClient.sendRequest('EditToteInOutboundManifest', data, storeId);
    }

    public static getToteIdsInOutboundManifest(storeId, amazonShipmentRefId) {
        const data = {
            storeId,
            amazonShipmentRefId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetToteIdsInOutboundManifest', data, storeId);
    }

    public static getOpenOutboundManifests(storeId) {
        const data = {
            storeId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetOpenOutboundManifests', data, storeId);
    }

    public static startOrContinuePackingToTote(storeId, toteId) {
        const data = {
            storeId,
            toteId,
        };
        return IhmSpyglassServiceClient.sendRequest('StartOrContinuePackingToTote', data, storeId);
    }

    public static getAttributeValueForFcSku(storeId, fcSku, attribute) {
        const data = {
            storeId,
            fcSku,
            attribute,
        };
        return IhmSpyglassServiceClient.sendRequest('GetAttributeValueForFcSku', data, storeId);
    }

    public static getConfirmedPOsForAsin(storeId, asin) {
        const data = {
            storeId,
            asin,
        };
        return IhmSpyglassServiceClient.sendRequest('GetConfirmedPOsForAsin', data, storeId);
    }

    public static getConfirmedPOsForVendor(storeId, vendorId) {
        const data = {
            storeId,
            vendorId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetConfirmedPOsForVendor', data, storeId);
    }

    public static getItemForPOAndFnSku(storeId, poId, fnSku) {
        const data = {
            storeId,
            poId,
            fnSku,
        };
        return IhmSpyglassServiceClient.sendRequest('GetItemForPOAndFnSku', data, storeId);
    }

    public static getPODetails(storeId, poId) {
        const data = {
            storeId,
            poId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetPODetails', data, storeId);
    }

    public static receiveAgainstPO(storeId, poId, fnSku, quantity, location, requestId, itemReceiptAttributes) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            const data = {
                storeId,
                poId,
                fnSku,
                quantity,
                location,
                requestId,
                associateAppEvent: appEvent,
                itemReceiptAttributes,
            };
            return IhmSpyglassServiceClient.sendRequest('ReceiveAgainstPO', data, storeId);
        });
    }

    public static receivePurchaseOrderItems(storeId, receivedLocation, receiptId, receivedItems) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            const data = {
                storeId,
                receivedLocation,
                receiptId,
                associateAppEvent: appEvent,
                receivedItems,
            };
            return IhmSpyglassServiceClient.sendRequest('ReceivePurchaseOrderItems', data, storeId);
        });
    }

    /**
     *  Set the package variation flag for given asin.
     */
    public static putPackageVariationForItemId(storeId, itemIdWithType, packageVariation) {
        const data = {
            storeId,
            itemIdWithType,
            packageVariation,
        };
        return IhmSpyglassServiceClient.sendRequest('PutPackageVariationForItemId', data, storeId);
    }

    /**
     *  Gets a list of items ( {"itemIdsWithType" : [{..},{..}]} ), that match the attributeKey and attribute Value
     *  provided.
     */
    public static getItemIdsForAttribute(storeId, attributeKey, attributeValue) {
        const data = {
            storeId,
            attributeKey,
            attributeValue,
        };

        return IhmSpyglassServiceClient.sendRequest('GetItemIdsForAttribute', data, storeId);
    }

    /**
     *  Returns friendly location data from Location Data Service for the given fixture entityId
     */
    public static getLocationDataByEntityId(storeId, entityId, businessType) {
        const data = {
            storeId,
            entityId,
            businessType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetLocationDataByEntityId', data, storeId);
    }

    /**
     *  Return location data in the provided store for the provided itemId
     */
    public static getLocationDataByItemId(storeId, asin, businessType) {
        const data = {
            storeId,
            asin,
            businessType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetLocationDataByItemId', data, storeId);
    }

    /**
     *  Set the Andon Cord flag for given asin.
     */
    public static putAndonCordForItemId(storeId, itemIdWithType, andonCord) {
        const data = {
            storeId,
            itemIdWithType,
            andonCord,
        };
        return IhmSpyglassServiceClient.sendRequest('PutAndonCordForItemId', data, storeId);
    }

    /**
     *  Get meta information for a review card.
     */
    public static getReviewCardInfo(storeId, itemIdWithType) {
        const data = {
            storeId,
            itemIdWithType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetReviewCardInfo', data, storeId);
    }

    /**
     *  Send review card to reprint.
     */
    public static flagReviewCardForReprint(storeId, asin, sectionName, sectionType, classification, needCorrection) {
        const data = {
            storeId,
            asin,
            sectionName,
            sectionType,
            classification,
            needCorrection,
        };
        return IhmSpyglassServiceClient.sendRequest('FlagReviewCardForReprint', data, storeId);
    }

    /**
     * Gets the cycle count id and status for the passed store id
     * @param {string} storeId - id of the store to get the cycle
     * count from
     *
     * @return cycleCountPromise {Promise} a cycleCountPromise promise
     */
    public static getW2WCycleCount(storeId, overrideBin) {
        const data = {
            storeId,
            overrideBin,
        };
        return IhmSpyglassServiceClient.sendRequest('GetW2WCycleCount', data, storeId);
    }

    /**
     * Gets the cycle count locations for a store id. This can be further
     * filtered by passing an optional asin and locationName
     *
     * @param {string} storeId - The id of the store to get the cycle count
     * locations from
     * @param {string} cycleCountId - The id of the cycle count
     * @param itemIdWithType {object} itemIdWithType of the item to get
     * locations from
     *
     * @return cycleCounLocationsPromise {Promise} a cycleCountLocations
     * promise
     */
    public static getW2WCycleCountLocations(storeId: string, cycleCountId: string, itemIdWithType?: object) {
        const data = {
            storeId,
            cycleCountId,
            ...(itemIdWithType && { itemIdWithType }),
        };
        return IhmSpyglassServiceClient.sendRequest('GetW2WCycleCountLocations', data, storeId);
    }

    /**
     * Adds / removes quantity from the record stating that the given
     * quantity of the given product was found at the given location. If
     * quantity is not provided for a REMOVE, it will remove all quantity
     * from the record. If there is no existing MarkedProduct record for the
     * ASIN-location pair (i.e. the product is untidy) then a new record is
     * written with untidy=true
     *
     * @param {string} storeId - The id of the store to get the cycle count
     * locations from
     * @param {string} cycleCountId - The id of the cycle count
     * @param {string} cycleCountStatus - The status of the cycle count as
     * observed by the client
     * @param {object} itemIdWithType - itemIdWithType of the product to
     * mark
     * @param {string} locationName - Location name of the found item
     * @param {string} action - Either "ADD" or "REMOVE"
     * @param {number} quantity - (optional for "REMOVE" actions) Quantity to add or remove
     */
    public static markW2WCycleCountProductAtLocation(
        storeId: string,
        cycleCountId: string,
        cycleCountStatus: string,
        itemIdWithType: object,
        locationName: string,
        action: string,
        quantity?: number,
    ) {
        const data = {
            storeId,
            cycleCountId,
            cycleCountStatus,
            itemIdWithType,
            locationName,
            action,
            ...(quantity && { quantity }),
        };
        return IhmSpyglassServiceClient.sendRequest('MarkW2WCycleCountProductAtLocation', data, storeId);
    }

    /**
     *  Expiration info related APIs
     */
    public static getAvailableExpirationDatesForItemId(storeId, itemIdWithType) {
        const data = {
            storeId,
            itemIdWithType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetAvailableExpirationDatesForItemId', data, storeId);
    }

    public static getFcSkusToAuditByDate(storeId, date) {
        const data = {
            storeId,
            date,
        };
        return IhmSpyglassServiceClient.sendRequest('GetFcSkusToAuditByDate', data, storeId);
    }

    public static getLocationsToAuditForItemId(storeId: string, itemIdWithType: object) {
        const data = {
            storeId,
            itemIdWithType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetLocationsToAuditForItemId', data, storeId);
    }

    public static incrementQuantityExpiringForLocationAndItemId(storeId, itemIdWithType, location, earliestExpirationDate, quantityToIncrement) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            const data = {
                storeId,
                itemIdWithType,
                location,
                earliestExpirationDate,
                quantityToIncrement,
                associateAppEvent: appEvent,
            };
            return IhmSpyglassServiceClient.sendRequest('IncrementQuantityExpiringForLocationAndItemId', data, storeId);
        });
    }

    public static setEarliestExpirationDateForLocationAndItemId(storeId, itemIdWithType, location, earliestExpirationDate, quantityExpiring) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            return IhmSpyglassServiceClient.sendRequest(
                'SetEarliestExpirationDateForLocationAndItemId',
                {
                    storeId,
                    itemIdWithType,
                    location,
                    earliestExpirationDate,
                    quantityExpiring,
                    associateAppEvent: appEvent,
                },
                storeId,
            );
        });
    }

    public static startAuditForItemId(storeId, itemIdWithType) {
        const data = {
            storeId,
            itemIdWithType,
        };
        return IhmSpyglassServiceClient.sendRequest('StartAuditForItemId', data, storeId);
    }

    public static getExpirationDateForItemId(storeId, toteId, itemIdWithType) {
        const data = {
            storeId,
            toteId,
            itemIdWithType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetExpirationDateForItemId', data, storeId);
    }

    /**
     * gets the lot container info, given the lot container code
     *
     * @param {string} storeId - The id of the store
     * @param {string} lotContainerCode - The id of the lot container
     *
     * @return getLotContainerInfoPromise {Promise}
     */
    public static getLotContainerInfo(storeId, lotContainerCode) {
        const data = {
            storeId,
            lotContainerCode,
        };

        return IhmSpyglassServiceClient.sendRequest('GetLotContainerInfo', data, storeId);
    }

    /**
     * gets the lot container info, given the lot container code
     *
     * @param {string} storeId - The id of the store
     * @param {string} lotContainerCode - The id of the lot container
     *
     * @return splitLotContainer {Promise}
     */
    public static splitLotContainer(storeId, inventoryContainer, sourceLotContainerCode, quantityToMove, destinationLotContainerCode, requestId) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            return IhmSpyglassServiceClient.sendRequest(
                'SplitLotContainer',
                {
                    storeId,
                    inventoryContainer,
                    sourceLotContainerCode,
                    quantityToMove,
                    destinationLotContainerCode,
                    requestId,
                    associateAppEvent: appEvent,
                },
                storeId,
            );
        });
    }

    /**
     * gets the lot container info, given the lot container code
     *
     * @param {string} storeId - The id of the store
     * @param {string} lotContainerCode - The id of the lot container
     *
     * @return combineLotContainers {Promise}
     */
    public static combineLotContainers(storeId, inventoryContainer, sourceLotContainerCode, quantityToMove, destinationLotContainerCode, requestId) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            return IhmSpyglassServiceClient.sendRequest(
                'CombineLotContainers',
                {
                    storeId,
                    inventoryContainer,
                    sourceLotContainerCode,
                    quantityToMove,
                    destinationLotContainerCode,
                    requestId,
                    associateAppEvent: appEvent,
                },
                storeId,
            );
        });
    }

    /**
     * gets the lot container info, given the lot container code
     *
     * @param {string} storeId - The id of the store
     * @param {string} sourceLotContainerCode - The id of the source lot container
     * @param {string} destinationLotContainerCode - The id of the destination lot container
     *
     */
    public static validateLotContainersCombineConditions(storeId, sourceLotContainerCode, destinationLotContainerCode) {
        return IhmSpyglassServiceClient.sendRequest(
            'ValidateLotContainersCombineConditions',
            {
                storeId,
                sourceLotContainerCode,
                destinationLotContainerCode,
            },
            storeId,
        );
    }

    /**
     * Fetches a description of the inventory expected and the inventory received for a Small Parcels package
     * @param {string} storeId - the id of the store
     * @param {string} trackingId - the carrier-provided trackingId
     * @returns shipmentId, packageId, itemDetailsList {Promise}
     */
    public static getPackageDetailsFromTrackingId(storeId, trackingId) {
        return IhmSpyglassServiceClient.sendRequest(
            'GetSmallParcelPackageDetails',
            {
                storeId,
                trackingId,
            },
            storeId,
        );
    }

    /**
     * Fetches a description of the inventory expected and the inventory received for a Small Parcels package
     * @param {string} storeId - the id of the store
     * @param {string} fulfillmentShipmentId - the fulfillmentShipmentId for the shipment package
     * @returns shipmentId, packageId, itemDetailsList {Promise}
     */
    public static getPackageDetailsFromFulfillmentShipmentId(storeId, fulfillmentShipmentId) {
        const data = {
            storeId,
            fulfillmentShipmentId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetSmallParcelPackageDetails', data, storeId);
    }

    /**
     * Sets the package status to "complete". This does not restrict further receives to the package
     * @param {string} storeId - the id of the store
     * @param {string} shipmentId - the shipmentId of the package
     * @param {int} packageId - the packageId of the package (package # within shipment)
     * @returns empty {Promise}
     */
    public static completePackage(storeId, shipmentId, packageId) {
        const data = {
            storeId,
            shipmentId,
            packageId,
        };
        return IhmSpyglassServiceClient.sendRequest('CompleteSmallParcelPackage', data, storeId);
    }

    /**
     * Receives inventory to Danbo
     * @param {string} storeId
     * @param {string} shipmentId
     * @param {int} packageId
     * @param {string} asin
     * @param {int} submittedQuantity
     * @param {int} quantityReceived
     * @param {string} submittedLocation
     * @param {string} user
     * @param {string}receiveApplication
     * @returns empty {Promise}
     */
    public static receiveItems(storeId, shipmentId, packageId, asin, submittedQuantity, quantityReceived, submittedLocation, user, receiveApplication) {
        const data = {
            storeId,
            shipmentId,
            packageId,
            asin,
            receivingQuantity: submittedQuantity,
            previouslyReceivedQuantity: quantityReceived,
            destinationLocation: submittedLocation,
            user,
            receiveApplication,
        };
        return IhmSpyglassServiceClient.sendRequest('ReceiveSmallParcelPackage', data, storeId);
    }

    /**
     * Receives inventory to Danbo. Identical to the receiveItems function
     * but with fnSku and merchantCustomerId input. Will replace receiveItems with SAPS
     * @param {string} storeId
     * @param {string} shipmentId
     * @param {int} packageId
     * @param {string} fnsku
     * @param {string} merchantCustomerId
     * @param {string} asin
     * @param {int} submittedQuantity
     * @param {int} quantityReceived
     * @param {string} submittedLocation
     * @param {string} user
     * @param {string}receiveApplication
     * @returns empty {Promise}
     */
    public static receiveSmallParcelPackage(
        storeId,
        shipmentId,
        packageId,
        fnSku,
        merchantCustomerId,
        asin,
        submittedQuantity,
        quantityReceived,
        submittedLocation,
        user,
        receiveApplication,
    ) {
        return IhmSpyglassServiceClient.sendRequest(
            'ReceiveSmallParcelPackage',
            {
                storeId,
                shipmentId,
                packageId,
                fnSku,
                merchantCustomerId,
                asin,
                receivingQuantity: submittedQuantity,
                previouslyReceivedQuantity: quantityReceived,
                destinationLocation: submittedLocation,
                user,
                receiveApplication,
            },
            storeId,
        );
    }

    /**
     * Receives all inventory in a small parcell package
     * @param {string} storeId
     * @param {string} shipmentId
     * @param {int} packageId
     * @param {string} user
     * @param {string} receiveApplication
     * @returns empty {Promise}
     */
    public static receiveAllItemsInSmallParcelPackage(storeId, shipmentId, packageId, user, receiveApplication) {
        return IhmSpyglassServiceClient.sendRequest(
            'ReceiveAllItemsInSmallParcelPackage',
            {
                storeId,
                shipmentId,
                packageId,
                user,
                receiveApplication,
            },
            storeId,
        );
    }

    /**
     * Search assortment by storeId, asin, timestamp and more.
     */
    public static searchAssortment(storeId, query) {
        return IhmSpyglassServiceClient.sendRequest(
            'SearchAssortment',
            {
                query,
            },
            storeId,
        );
    }

    /**
     * Requests replacement(s) for a set of review cards.
     * @param {string} asin - asin of review cards to be replaced
     * @param {string} storeId - storeId for store requesting replacement. Used for getting warehouseId
     * @param {List<ReplacementLocation>} replacementLocations - List of ReplacementLocation Object
     * containing a section (category/subcategory), faceoutDiff and inWindow (to be removed)
     * @param {string} reason - reason for requesting replacement. [missing/damaged]
     * @param {string} requestId - requestId generated per call
     * @returns empty {Promise}
     */
    public static createReviewCardReplacements(asin, storeId, replacementLocations, reason, requestId) {
        const data = {
            asin,
            storeId,
            replacementLocations,
            reason,
            requestId,
        };
        return IhmSpyglassServiceClient.sendRequest('CreateReviewCardReplacements', data, storeId);
    }

    /**
     * Break open some quantity of an item in a bin/tote into child items
     *
     * @param locationId - yemi location id of a bin or transship tote
     * @param itemIdWithType - asin or fcsku
     * @param quantityToBreakOpen - DecimalMeasure indicating the quantity to break open
     */
    public static breakOpenItems(storeId, locationId, itemIdWithType, quantityToBreakOpen, requestId, eventId) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            if (eventId) {
                appEvent.eventId = eventId;
            }
            return IhmSpyglassServiceClient.sendRequest(
                'BreakOpenItems',
                {
                    storeId,
                    locationId,
                    itemIdWithType,
                    quantityToBreakOpen,
                    requestId,
                    associateAppEvent: appEvent,
                },
                storeId,
            );
        });
    }
    /**
     * Retrieve a list of scheduled Dock shipments for a given store from IhmDockReceiveService
     * @param {String} storeId - store for which shipments are scheduled
     * @param {Timestamp} start - epoch in millis, the beginning of the time window
     * within which shipments are scheduled for delivery
     * @param {Timestamp} end - epoch in millis, the ending of the time
     * window within which shipments are scheduled for delivery
     * @returns {Promise} list of objects containing details of scheduled deliveries
     * includes shipmentId(PO id or tranship manifest id), shipmentType
     * (PO or Transshipment), and scheduled delivery date
     */
    public static getInboundAppointmentsForTimeWindow(storeId, start, end) {
        return IhmSpyglassServiceClient.sendRequest(
            'GetInboundAppointmentsForTimeWindow',
            {
                timeWindow: {
                    start,
                    end,
                },
            },
            storeId,
        );
    }

    /**
     * Start a DockReceipt for a Purchase Order or Transshipment
     * @param {String} storeId - store for which shipments are checked in
     * @param {String} appointmentId - the appointmentId scheduled for this dock receive check in.
     * @param {String} shipmentId - the shipmentId for this particular PO or transshipment.
     * @param {String} dockEntityType - enum used in DockReceiveService, 'PO' or "TRANSSHIP'
     * @returns {Promise} receiptId - a unique identifier for this DockReceipt
     * that is used to update/add temperature samples
     */
    public static startDockReceipt(storeId, appointmentId, shipmentId, dockEntityType) {
        return IhmSpyglassServiceClient.sendRequest(
            'StartDockReceipt',
            {
                storeId,
                appointmentId,
                shipmentId,
                dockEntityType,
            },
            storeId,
        );
    }

    /**
     * Add a temperature sample to a DockReceipt
     * @param {String} storeId - store for which a DockReceipt has been created
     * @param {String} receiptId - the receipt ID to which a sample is to be saved
     * @param {Object} temperatureSample - the location, value, units, date, zone of a
     * temperature sample defined in ihmdockreceive.coral
     * @returns {Promise} isTemperatureAcceptableForZone - validation by
     * DockReceiveService if acceptable for Food Safety
     */
    public static addTemperatureSampleToDockReceipt(storeId, receiptId, temperatureSample) {
        return IhmSpyglassServiceClient.sendRequest(
            'AddTemperatureSampleToDockReceipt',
            {
                storeId,
                receiptId,
                temperatureSample,
            },
            storeId,
        );
    }

    /**
     *  Close a DockReceipt for a Purchase Order or Transshipment
     *  @param {String} storeId - store for which a DockReceipt should be closed
     *  @param {String} receiptId - the receipt ID to identify which DockReceipt to close
     *  @param {Promise} void - "CloseDockReceiptResponse" is empty https://code.amazon.com/reviews/CR-8643678
     */
    public static closeDockReceipt(storeId, receiptId, notes) {
        return IhmSpyglassServiceClient.sendRequest(
            'CloseDockReceipt',
            {
                storeId,
                receiptId,
                notes,
            },
            storeId,
        );
    }

    /**
     * Transform an item from a source asin to the target asin.
     * @param {String} storeId - store for which the transformation is happening in
     * @param {Object} sourceInventory - the asin, location, and quantity that is being transformed
     * @param {Object} targetInventory - the asin, location, and quantity that the source asin will be transformed into
     * @returns {Promise} void - "TransformItemsOutput" is empty
     */
    public static transformItems(storeId, sourceInventory, targetInventory, eventId) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            if (eventId) {
                appEvent.eventId = eventId;
            }
            return IhmSpyglassServiceClient.sendRequest(
                'TransformItems',
                {
                    storeId,
                    sourceInventory,
                    targetInventory,
                    associateAppEvent: appEvent,
                },
                storeId,
            );
        });
    }

    /**
     * Purges bound inventory for a given item and bin
     */
    public static purgeBoundInventory(storeId, itemIdWithType, quantity, binId, bindingData, iog) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            return IhmSpyglassServiceClient.sendRequest(
                'PurgeBoundInventory',
                {
                    storeId,
                    itemIdWithType,
                    quantity,
                    binId,
                    bindingData,
                    iog,
                    associateAppEvent: appEvent,
                },
                storeId,
            ).then((data) => {
                AppInfoLoader.getAppConfig().then((configData) => {
                    new CountMetric(storeId, 'BoundItemsPurged', 'IhmFlyingDutchman', configData.toolName).count(quantity.value);
                });
                return data;
            });
        });
    }

    /**
     * Moves bound inventory from srcLocId to dstLocId for a given item
     */
    public static moveBoundItemByItemId(storeId, srcLocId, dstLocId, itemIdWithType, moveQuantity, expectedSrcQuantity, targetExpirationDate, bindingData, retailPrice?: any) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            return IhmSpyglassServiceClient.sendRequest(
                'MoveBoundItemByItemId',
                {
                    storeId,
                    srcLocId,
                    dstLocId,
                    itemIdWithType,
                    moveQuantity,
                    expectedSrcQuantity,
                    targetExpirationDate,
                    bindingData,
                    associateAppEvent: appEvent,
                    retailPrice,
                },
                storeId,
            ).then((data) => {
                AppInfoLoader.getAppConfig().then((configData) => {
                    new CountMetric(storeId, 'BoundItemsMoved', 'IhmFlyingDutchman', configData.toolName).count(moveQuantity.value);
                });
                return data;
            });
        });
    }

    /**
     * Return WfmPOIdMap given wfmPOId
     */
    public static getWfmPOIdMapForWfmPOId(libbyStoreId, wfmOrderId) {
        const data = {
            libbyStoreId,
            wfmOrderId,
        };
        return client.sendRequest({ operation: 'GetWfmPOIdMap', data }, libbyStoreId);
    }

    /**
     * Moves an item by an item id and returns a promise to the result.
     *
     * @param moveQuantity - DecimalMeasure indicating the quantity to move.
     */

    public static moveItemByItemId(
        storeId: string,
        srcLocId: string,
        dstLocId: string,
        itemIdWithType: { [key: string]: string },
        moveQuantity: any,
        expectedSrcQuantity?: any,
        allowOverage?: any,
        breakOpen?: any,
        requestId?: string,
        targetExpirationDate?: any,
        eventId?: any,
        itemExpirationDate?: any,
        retailPrice?: any,
        targetRetailPrice?: any,
        reason?: string,
    ) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            if (eventId) {
                appEvent.eventId = eventId;
            }
            return IhmSpyglassServiceClient.sendRequest(
                'MoveItemByItemId',
                {
                    storeId,
                    srcLocId,
                    dstLocId,
                    itemIdWithType,
                    moveQuantity,
                    expectedSrcQuantity,
                    allowOverage,
                    breakOpen,
                    requestId,
                    targetExpirationDate,
                    associateAppEvent: appEvent,
                    itemExpirationDate,
                    retailPrice,
                    targetRetailPrice,
                    reason,
                },
                storeId,
            ).then((data) => {
                AppInfoLoader.getAppConfig().then((configData) => {
                    new CountMetric(storeId, 'ItemsMoved', 'IhmFlyingDutchman', configData.toolName).count(moveQuantity.value);
                });
                return data;
            });
        });
    }

    /**
     *  Purges inventory for a given item and bin.
     *  @param {string} storeId - The store ID
     *  @param {object} itemIdWithType - Item identifier with type (e.g. ASIN, FCSKU)
     *  @param {string} reason - Formatted removal reason code
     *  @param {object} quantity - Quantity to purge with value and unit
     *  @param {string} binId - The bin/location ID to purge from
     *  @param {string} orderId - Optional order ID for vendor order purges
     *  @param {string} vendorCode - Optional vendor code for vendor order purges
     *  @param {boolean | null} [shouldUseVoidCostInfo] - Optional flag to use void cost structure for QC WFM hotbar ASINs. Pass null or omit to exclude from request.
     */
    public static purgeInventoryInBin(storeId, itemIdWithType, reason, quantity, binId, orderId?, vendorCode?, shouldUseVoidCostInfo?) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            return IhmSpyglassServiceClient.sendRequest(
                'PurgeInventoryInBin',
                {
                    storeId,
                    itemIdWithType,
                    reason,
                    quantity,
                    binId,
                    associateAppEvent: appEvent,
                    ...(orderId && { orderId }),
                    ...(vendorCode && { vendorCode }),
                    ...(shouldUseVoidCostInfo !== null && shouldUseVoidCostInfo !== undefined && { shouldUseVoidCostInfo }),
                },
                storeId,
            ).then((data) => {
                AppInfoLoader.getAppConfig().then((configData) => {
                    new CountMetric(storeId, 'ItemsPurged', 'IhmFlyingDutchman', configData.toolName).count(quantity.value);
                });
                return data;
            });
        });
    }

    public static getCategoryInfo(storeId: string, itemIdWithType: { [key: string]: string }, seekToDate?: any) {
        const data = {
            storeId,
            itemIdWithType,
            seekToDate,
        };
        return IhmSpyglassServiceClient.sendRequest('GetCategoryInfo', data, storeId);
    }

    /**
     * Receive Transship inventory.
     */
    public static receiveTransshipInventory(storeId, toteId1, toteId2) {
        return AssociateAppEvent.constructAppEvent().then((appEvent) => {
            const data = {
                storeId,
                toteId1,
                toteId2,
                associateAppEvent: appEvent,
            };
            return IhmSpyglassServiceClient.sendRequest('ReceiveTransshipInventory', data, storeId);
        });
    }

    public static getAvailabilityForPlanning(storeId, fnSku) {
        const data = {
            storeId,
            fnSku,
        };
        return IhmSpyglassServiceClient.sendRequest('GetAvailabilityForPlanning', data, storeId);
    }

    public static getAttributeValuesForItemIds(storeId, itemIdWithType) {
        const data = {
            storeId,
            itemIdWithType,
        };
        return IhmSpyglassServiceClient.sendRequest('GetAttributeValuesForItemIds', data, storeId);
    }

    public static configureMarkdown(requestId, storeId, itemId, itemIdType, expirationTime, scancodes) {
        const data = {
            requestId,
            storeId,
            itemId,
            itemIdType,
            expirationTime,
            scancodes,
        };
        return IhmSpyglassServiceClient.sendRequest('ConfigureMarkdown', data, storeId);
    }

    public static getPromoAsins(storeId) {
        const data = {
            storeId,
        };
        return IhmSpyglassServiceClient.sendRequest('GetPromoAsins', data, storeId);
    }
}
